import { Point2D } from '../types';
import { SVGRenderer } from '../core/SVGRenderer';

export class GeometryLayoutSolver {
  private points: Map<string, Point2D> = new Map();
  private arcs: Map<string, { center: Point2D; radius: number; startAngle: number; endAngle: number }> = new Map();
  
  private baseOrigin: Point2D = { x: 250, y: 420 };
  private defaultRadius: number = 120;
  private defaultLength: number = 280;

  constructor(baseOrigin?: Point2D, defaultRadius: number = 120) {
    if (baseOrigin) this.baseOrigin = { ...baseOrigin };
    this.defaultRadius = defaultRadius;
  }

  public setPoint(name: string, pos: Point2D): void {
    this.points.set(name.toUpperCase(), { ...pos });
  }

  public getPoint(name: string): Point2D {
    const key = name.toUpperCase();
    if (!this.points.has(key)) {
      if (this.points.size === 0) {
        this.points.set(key, { ...this.baseOrigin });
      } else if (key === 'B') {
        const pA = this.getPoint('A');
        this.points.set('B', { x: pA.x + this.defaultLength, y: pA.y });
      } else if (key === 'O') {
        this.points.set('O', { x: 250, y: 420 });
      } else {
        const last = Array.from(this.points.values())[this.points.size - 1];
        this.points.set(key, { x: last.x + 100, y: last.y });
      }
    }
    return this.points.get(key)!;
  }

  public hasPoint(name: string): boolean {
    return this.points.has(name.toUpperCase());
  }

  public solveRay(startName: string, passName: string, angleRad?: number): { pStart: Point2D; pPass: Point2D } {
    const pStart = this.getPoint(startName);
    let pPass: Point2D;

    if (this.hasPoint(passName)) {
      pPass = this.getPoint(passName);
    } else {
      let angle = angleRad;
      if (angle === undefined) {
        const passUpper = passName.toUpperCase();
        if (passUpper === 'B' || passUpper === 'A') {
          angle = 0; // 0 deg horizontal right
        } else if (passUpper === 'P7' || passUpper === 'P5' || passUpper === 'C45' || passUpper === 'C') {
          angle = -Math.PI / 4; // 45 deg UPWARDS
        } else if (passUpper === 'E') {
          angle = -Math.PI / 3; // 60 deg UPWARDS
        } else if (passUpper === 'P4' || passUpper === 'D90' || passUpper === 'D') {
          angle = -Math.PI / 2; // 90 deg UPWARDS
        } else if (passUpper === 'R' || passUpper === 'P30') {
          angle = -Math.PI / 6; // 30 deg UPWARDS
        } else if (passUpper === 'P75') {
          angle = -Math.PI * 5 / 12; // 75 deg
        } else if (passUpper === 'P105') {
          angle = -Math.PI * 7 / 12; // 105 deg
        } else if (passUpper === 'P135') {
          angle = -Math.PI * 3 / 4; // 135 deg
        } else {
          angle = 0;
        }
      }
      pPass = SVGRenderer.polarToCartesian(pStart, this.defaultLength, angle);
      this.setPoint(passName, pPass);
    }

    return { pStart, pPass };
  }

  public solveSegment(startName: string, endName: string): { pStart: Point2D; pEnd: Point2D } {
    const pStart = this.getPoint(startName);
    let pEnd: Point2D;

    if (this.hasPoint(endName)) {
      pEnd = this.getPoint(endName);
    } else {
      pEnd = { x: pStart.x + this.defaultLength, y: pStart.y };
      this.setPoint(endName, pEnd);
    }

    return { pStart, pEnd };
  }

  public solveArcWithIntersection(options: {
    centerName: string;
    radiusSpec?: string | number;
    intersectsTarget?: string | string[];
    intersectionPointName?: string;
    intersectionPoints?: string[];
    side?: 'above' | 'below' | 'both';
  }): Array<{ center: Point2D; radius: number; startAngle: number; endAngle: number; intersection?: Point2D; intersectionName?: string }> {
    const center = this.getPoint(options.centerName);
    let radius = this.defaultRadius;

    // Handle radius specification
    if (typeof options.radiusSpec === 'number') {
      radius = options.radiusSpec;
    } else if (options.radiusSpec === 'AB' || (this.hasPoint('A') && this.hasPoint('B') && (options.intersectionPointName === 'C' || options.intersectionPointName === 'D') && !options.intersectsTarget?.toString().includes('OA'))) {
      const pA = this.getPoint('A');
      const pB = this.getPoint('B');
      radius = SVGRenderer.distance(pA, pB);
    }

    const results: Array<{ center: Point2D; radius: number; startAngle: number; endAngle: number; intersection?: Point2D; intersectionName?: string }> = [];

    // Perpendicular Bisector arcs (side: 'both')
    if (options.side === 'both') {
      const pA = this.getPoint('A');
      const pB = this.getPoint('B');
      const mid = { x: (pA.x + pB.x) / 2, y: (pA.y + pB.y) / 2 };
      const halfAB = SVGRenderer.distance(pA, pB) / 2;
      const h = Math.sqrt(Math.max(0, radius * radius - halfAB * halfAB));

      const pAbove = { x: mid.x, y: mid.y - h };
      const pBelow = { x: mid.x, y: mid.y + h };

      const inter1Name = options.intersectionPoints?.[0] || 'P';
      const inter2Name = options.intersectionPoints?.[1] || 'Q';

      if (options.centerName === 'B') {
        this.setPoint(inter1Name, pAbove);
        this.setPoint(inter2Name, pBelow);
      }

      const angAbove = Math.atan2(pAbove.y - center.y, pAbove.x - center.x);
      results.push({
        center,
        radius,
        startAngle: angAbove + 0.35,
        endAngle: angAbove - 0.35,
        intersection: pAbove,
        intersectionName: inter1Name,
      });

      const angBelow = Math.atan2(pBelow.y - center.y, pBelow.x - center.x);
      results.push({
        center,
        radius,
        startAngle: angBelow - 0.35,
        endAngle: angBelow + 0.35,
        intersection: pBelow,
        intersectionName: inter2Name,
      });

      return results;
    }

    // Handle multiple intersection points (e.g. primary arc cutting both AB and AC at P5 & P6 or P1 & P4)
    if (options.intersectionPoints && options.intersectionPoints.length > 1) {
      const pts = options.intersectionPoints;
      // Arc from A cutting baseline AB at pts[0] (0 deg) and 90 deg ray at pts[1] (-90 deg)
      const p1 = SVGRenderer.polarToCartesian(center, radius, 0);
      const p2 = SVGRenderer.polarToCartesian(center, radius, -Math.PI / 2);
      this.setPoint(pts[0], p1);
      this.setPoint(pts[1], p2);

      results.push({
        center,
        radius,
        startAngle: 0.2,
        endAngle: -Math.PI / 2 - 0.2,
        intersection: p1,
        intersectionName: pts[0],
      });
      results.push({
        center,
        radius,
        startAngle: -Math.PI / 2 + 0.2,
        endAngle: -Math.PI / 2 - 0.2,
        intersection: p2,
        intersectionName: pts[1],
      });

      return results;
    }

    let intersection: Point2D | undefined;
    let startAngle = 0;
    let endAngle = -Math.PI * 0.45;

    // 45 deg step 3/4: Arcs from P1(0 deg) and P4(90 deg) intersecting at P7 / C (45 deg point)
    if ((options.centerName === 'P1' || options.centerName === 'P4' || options.centerName === 'P5' || options.centerName === 'P6') && (options.intersectionPointName === 'P7' || options.intersectionPointName === 'C' || options.intersectionPointName === 'P5')) {
      const pA = this.getPoint('A');
      // Exact intersection at 45 deg: distance from A is 2 * radius * cos(45/2 deg) = 221.7
      const distA_P7 = 2 * radius * Math.cos(Math.PI / 8);
      const pP7 = SVGRenderer.polarToCartesian(pA, distA_P7, -Math.PI / 4);
      const targetName = options.intersectionPointName || 'P7';
      this.setPoint(targetName, pP7);

      const targetAngle = Math.atan2(pP7.y - center.y, pP7.x - center.x);
      startAngle = targetAngle + 0.35;
      endAngle = targetAngle - 0.35;
      intersection = pP7;
    }
    // Equilateral Triangle arc intersection at C (from A and B with radius = AB)
    else if (options.intersectionPointName === 'C' && (options.centerName === 'A' || options.centerName === 'B') && !this.hasPoint('P4')) {
      const pA = this.getPoint('A');
      const pB = this.getPoint('B');
      const abLen = SVGRenderer.distance(pA, pB);
      radius = abLen;

      // C is equilateral apex above AB: (A.x + abLen/2, A.y - abLen * sqrt(3)/2)
      const pC = { x: pA.x + abLen / 2, y: pA.y - abLen * Math.sqrt(3) / 2 };
      this.setPoint('C', pC);

      const targetAngle = Math.atan2(pC.y - center.y, pC.x - center.x);
      startAngle = targetAngle + 0.35;
      endAngle = targetAngle - 0.35;
      intersection = pC;
    }
    // Primary arc centered at O for Angle Bisector (creates both P on OA and Q on OB)
    else if (options.centerName === 'O' && !options.intersectsTarget) {
      const pO = this.getPoint('O');
      const pP = SVGRenderer.polarToCartesian(pO, radius, 0); // 0 deg
      const pQ = SVGRenderer.polarToCartesian(pO, radius, -Math.PI / 3); // -60 deg
      this.setPoint('P', pP);
      this.setPoint('Q', pQ);

      startAngle = 0.2;
      endAngle = -Math.PI / 3 - 0.2; // sweeps from +11 deg to -71 deg covering both arms
      intersection = pP;
    }
    // Angle Bisector arcs from P and Q intersecting at R
    else if ((options.centerName === 'P' || options.centerName === 'Q') && options.intersectionPointName === 'R') {
      const pO = this.getPoint('O');
      // R is exact intersection of circle(P, radius) & circle(Q, radius)
      // Distance OR = 2 * radius * cos(30 deg) = 2 * 120 * 0.866025 = 207.846
      const distO_R = 2 * radius * Math.cos(Math.PI / 6);
      const pR = SVGRenderer.polarToCartesian(pO, distO_R, -Math.PI / 6);
      this.setPoint('R', pR);

      const targetAngle = Math.atan2(pR.y - center.y, pR.x - center.x);
      startAngle = targetAngle + 0.35;
      endAngle = targetAngle - 0.35;
      intersection = pR;
    }
    // 90 deg step 5: Arcs from P2 and P3 intersecting at P4
    else if ((options.centerName === 'P2' || options.centerName === 'P3') && (options.intersectionPointName === 'P4' || options.intersectsTarget?.toString().includes('P3'))) {
      const pA = this.getPoint('A');
      // Exact intersection P4 at (250, 212.15) directly 90 deg above A
      const pP4 = { x: pA.x, y: pA.y - 207.846 };
      this.setPoint('P4', pP4);

      const targetAngle = Math.atan2(pP4.y - center.y, pP4.x - center.x);
      startAngle = targetAngle + 0.35;
      endAngle = targetAngle - 0.35;
      intersection = pP4;
    }
    // 90 deg step 4: Arc from P2 intersecting primary arc at P3 (120 deg point)
    else if (options.centerName === 'P2' && options.intersectionPointName === 'P3') {
      const pA = this.getPoint('A');
      const pP3 = SVGRenderer.polarToCartesian(pA, radius, -Math.PI * 2 / 3); // -120 deg
      this.setPoint('P3', pP3);

      const targetAngle = Math.atan2(pP3.y - center.y, pP3.x - center.x);
      startAngle = targetAngle + 0.35;
      endAngle = targetAngle - 0.35;
      intersection = pP3;
    }
    // 60 deg or 90 deg step 3: Arc from P1 or D intersecting primary arc at P2 or E (60 deg point)
    else if (options.intersectsTarget === 'previous' || options.intersectsTarget?.toString().startsWith('arc')) {
      const pA = this.hasPoint('O') ? this.getPoint('O') : this.getPoint('A');
      const angle60 = -Math.PI / 3; // 60 deg UPWARDS
      intersection = SVGRenderer.polarToCartesian(pA, radius, angle60);

      const interName = options.intersectionPointName || 'E';
      this.setPoint(interName, intersection);

      const baseAngle = Math.atan2(intersection.y - center.y, intersection.x - center.x);
      startAngle = baseAngle + 0.35;
      endAngle = baseAngle - 0.35;
    }
    // Primary arc on baseline ray (e.g. AB or OA)
    else if (options.intersectsTarget && options.intersectsTarget.toString().length >= 2) {
      const lineStart = this.getPoint(options.intersectsTarget.toString()[0]);
      const linePass = this.getPoint(options.intersectsTarget.toString()[1]);
      const lineAngle = Math.atan2(linePass.y - lineStart.y, linePass.x - lineStart.x);

      intersection = SVGRenderer.polarToCartesian(center, radius, lineAngle);
      if (options.intersectionPointName) {
        this.setPoint(options.intersectionPointName, intersection);
      }

      if (options.intersectionPointName === 'P1') {
        startAngle = lineAngle + 0.2;
        endAngle = lineAngle - Math.PI * 0.75; // sweeps past 120 deg
      } else {
        startAngle = lineAngle + 0.25;
        endAngle = lineAngle - Math.PI * 0.48;
      }
    }

    if (options.intersectionPointName && intersection) {
      this.setPoint(options.intersectionPointName, intersection);
    }

    results.push({
      center,
      radius,
      startAngle,
      endAngle,
      intersection,
      intersectionName: options.intersectionPointName,
    });

    return results;
  }
}
